import { inject, Injector } from '@angular/core';
import { signalStoreFeature } from '@ngrx/signals';
import {
  Events,
  injectDispatch,
  withEventHandlers,
} from '@ngrx/signals/events';
import { authEvents } from './events';
import { catchError, from, of, switchMap, tap, throwError } from 'rxjs';
import { AuthService } from '../../data-access/auth';
import { isAuthFatalError, isCognitoTokenError } from '../../helpers/auth-session';
import { AUTH_APP_FLOW, AuthAppFlow } from '../../tokens/auth-app-flow';
import { AuthUser } from '../../models/auth';
import { ToastService } from '../../services/toast';
import { UserNavService } from '../../services/user-nav';
import { UserTypeEnum } from '../../enums/auth';
import { tapResponse } from '@ngrx/operators';
import { AUTH_STORAGE_KEYS } from '../../constants/auth-storage';
import { CleanUpService } from '../../services/cleanup';
import { FirebaseMessagingService } from '../../services/firebase-messaging';
import { NotificationApiService } from '../../data-access/notification';
type AfterUserOptions = { silent: boolean; flow: AuthAppFlow };

type AuthDispatchShape = {
  getUserSuccess: (u: AuthUser) => void;
  loginSuccess: (b: boolean) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setResendConfirmation: (value: boolean) => void;
};

function isRoleAllowedForFlow(
  role: UserTypeEnum,
  flow: AuthAppFlow,
): boolean {
  switch (flow) {
    case 'admin':
      return role === UserTypeEnum.SUPER_ADMIN;
    case 'fleet':
      return (
        role === UserTypeEnum.CARRIER_ADMIN ||
        role === UserTypeEnum.CARRIER_MANAGER ||
        role === UserTypeEnum.CARRIER_ITURAN
      );
    case 'freight':
      return role !== UserTypeEnum.SUPER_ADMIN;
  }
}

function denyUnauthorizedUser(
  authDispatch: AuthDispatchShape,
  messageService: ToastService,
): void {
  messageService.show({
    type: 'error',
    message: 'Access Denied',
    detail: 'Unauthorized user',
    duration: 7000,
  });
  authDispatch.logout();
}

function afterUserAttributesLoaded(
  userObject: AuthUser,
  opts: AfterUserOptions,
  authDispatch: AuthDispatchShape,
  messageService: ToastService,
  navigationService: UserNavService,
): void | Promise<boolean> {
  const attrs = Object.values(userObject);
  const allTruthy = attrs.every((v) => v !== '' && v != null);
  if (!allTruthy) {
    return;
  }

  const role = userObject['custom:user_type'] as UserTypeEnum;
  if (!isRoleAllowedForFlow(role, opts.flow)) {
    denyUnauthorizedUser(authDispatch, messageService);
    return;
  }

  authDispatch.getUserSuccess(userObject);
  authDispatch.loginSuccess(true);

  if (opts.flow === 'fleet') {
    // Fleet app navigates to /continue then opens Ituran (see FleetLoginForm).
    return;
  }

  if (opts.silent) {
    return;
  }

  if (opts.flow === 'freight') {
    messageService.show({
      message: 'Login successful',
      type: 'success',
    });
  }

  const requestedUrl = localStorage.getItem(AUTH_STORAGE_KEYS.requestedUrl);
  if (requestedUrl) {
    localStorage.removeItem(AUTH_STORAGE_KEYS.requestedUrl);
    return navigationService.navigateTo(requestedUrl);
  }
  return navigationService.navigateByRole(role);
}

function handleCredentialLoginError(
  error: unknown,
  authDispatch: AuthDispatchShape,
  messageService: ToastService,
  navigationService: UserNavService,
): void {
  const err = error as Error & { name?: string; code?: string };
  const errorCode = err?.name || err?.code;
  if (errorCode === 'UserNotConfirmedException') {
    authDispatch.setResendConfirmation(true);
  }
  if (
    errorCode === 'NotAuthorizedException' &&
    err?.message === 'User is disabled.'
  ) {
    void navigationService.navigateTo('/disabled-user');
  }
  messageService.show({
    message: 'Login Failed',
    detail: `${err?.message ?? 'Unknown error'}`,
    type: 'error',
    duration: 7000,
  });
  authDispatch.setLoading(false);
}

function loginWithCredentials$(
  username: string,
  password: string,
  flow: AuthAppFlow,
  deps: {
    authService: AuthService;
    authDispatch: AuthDispatchShape;
    messageService: ToastService;
    navigationService: UserNavService;
  },
) {
  const { authService, authDispatch, messageService, navigationService } = deps;

  return authService.login(username, password).pipe(
    switchMap((commandOutput) => {
      const result = commandOutput.AuthenticationResult;
      if (!result?.AccessToken || !authService.saveSession(result)) {
        return throwError(() => new Error('Missing authentication result'));
      }
      return authService.getUserInfo(result.AccessToken);
    }),
    tapResponse({
      next: (getUserOutput) => {
        const attrs = getUserOutput.UserAttributes;
        if (!attrs || attrs.length === 0) {
          authDispatch.setLoading(false);
          return;
        }
        const allTruthy = attrs.every((item) => Boolean(item.Value));
        if (!allTruthy) {
          authDispatch.setLoading(false);
          return;
        }
        const userObject = authService.authUserFromAttr(attrs);
        const navOrVoid = afterUserAttributesLoaded(
          userObject,
          { silent: false, flow },
          authDispatch,
          messageService,
          navigationService,
        );
        void Promise.resolve(navOrVoid).finally(() => {
          authDispatch.setLoading(false);
        });
      },
      error: (error) => {
        handleCredentialLoginError(
          error,
          authDispatch,
          messageService,
          navigationService,
        );
      },
    }),
  );
}

export function withAuthEffects() {
  return signalStoreFeature(
    withEventHandlers(() => {
      const events = inject(Events);
      const authService = inject(AuthService);
      const authDispatch = injectDispatch(authEvents);
      const navigationService = inject(UserNavService);
      const messageService = inject(ToastService);
      const cleanupService = inject(CleanUpService);
      const injector = inject(Injector);
      const notificationApi = inject(NotificationApiService);
      const authAppFlow = inject(AUTH_APP_FLOW);

      const loginDeps = {
        authService,
        authDispatch,
        messageService,
        navigationService,
      };

      return {
        adminLogin$: events.on(authEvents.adminLogin).pipe(
          tap(() => authDispatch.setLoading(true)),
          switchMap(({ payload: { username, password } }) =>
            loginWithCredentials$(username, password, 'admin', loginDeps),
          ),
        ),
        fleetlogin$: events.on(authEvents.fleetlogin).pipe(
          tap(() => authDispatch.setLoading(true)),
          switchMap(({ payload: { username, password } }) =>
            loginWithCredentials$(username, password, 'fleet', loginDeps),
          ),
        ),
        freightLogin$: events.on(authEvents.freightLogin).pipe(
          tap(() => authDispatch.setLoading(true)),
          switchMap(({ payload: { username, password } }) =>
            loginWithCredentials$(username, password, 'freight', loginDeps),
          ),
        ),
        signUp$: events.on(authEvents.signUp).pipe(
          tap(() => authDispatch.setLoading(true)),
          switchMap(
            ({
              payload: {
                username,
                email,
                phoneNumber,
                password,
                givenName,
                userType,
              },
            }) =>
              authService
                .signUp(
                  username,
                  phoneNumber,
                  email,
                  password,
                  givenName,
                  userType,
                )
                .pipe(
                  tapResponse({
                    next: () => {
                      authDispatch.signUpSuccess();
                      messageService.show({
                        type: 'success',
                        message: 'Signup Successful',
                        detail: 'Check your email to verify your account',
                        duration: 5000,
                      });
                      void navigationService.navigateTo('/login');
                    },
                    error: (error) => {
                      const err = error as Error;
                      messageService.show({
                        type: 'error',
                        message: 'Signup Failed',
                        detail: err?.message ?? String(error),
                        duration: 5000,
                      });
                    },
                    finalize: () => authDispatch.setLoading(false),
                  }),
                ),
          ),
        ),
        resetPassword$: events.on(authEvents.resetPassword).pipe(
          tap(() => authDispatch.setLoading(true)),
          switchMap(({ payload: email }) =>
            authService.forgotPassword(email).pipe(
              tapResponse({
                next: () => {
                  messageService.show({
                    type: 'success',
                    message: 'Success.',
                    detail: 'Confirmation code sent to email',
                  });
                  void navigationService.navigateTo('/password-reset/confirm');
                },
                error: (error) => {
                  const err = error as Error;
                  messageService.show({
                    type: 'error',
                    message: 'Password Reset Failure',
                    detail: err?.message ?? String(error),
                    duration: 5000,
                  });
                },
                finalize: () => authDispatch.setLoading(false),
              }),
            ),
          ),
        ),
        confirmPasswordReset$: events.on(authEvents.confirmPasswordReset).pipe(
          tap(() => authDispatch.setLoading(true)),
          switchMap(({ payload: { code, email, newPassword } }) =>
            authService.confirmPasswordReset(email, code, newPassword).pipe(
              tapResponse({
                next: () => {
                  messageService.show({
                    type: 'success',
                    message: 'Success.',
                    detail: 'Password reset confirmed successfully',
                  });
                  void navigationService.navigateTo('/login');
                },
                error: (error) => {
                  const err = error as Error;
                  messageService.show({
                    type: 'error',
                    message: 'Password reset confirmation Failure',
                    detail: err?.message ?? String(error),
                    duration: 5000,
                  });
                },
                finalize: () => authDispatch.setLoading(false),
              }),
            ),
          ),
        ),
        resendConfirmationEmail$: events
          .on(authEvents.resendConfirmationEmail)
          .pipe(
            tap(() => authDispatch.setLoading(true)),
            switchMap(({ payload: email }) =>
              authService.resendConfirmation(email).pipe(
                tapResponse({
                  next: () => {
                    messageService.show({
                      type: 'success',
                      message: 'Success.',
                      detail: 'Check your email to verify your account',
                    });
                    authDispatch.setResendConfirmation(false);
                  },
                  error: (error) => {
                    const err = error as Error;
                    messageService.show({
                      type: 'error',
                      message: 'Failed to send confirmation email.',
                      detail: err?.message ?? String(error),
                      duration: 5000,
                    });
                  },
                  finalize: () => authDispatch.setLoading(false),
                }),
              ),
            ),
          ),
        respondToNewPasswordChalleng$: events
          .on(authEvents.respondToNewPasswordChalleng)
          .pipe(
            tap(() => authDispatch.setLoading(true)),
            switchMap(({ payload: { username, newPassword } }) =>
              authService
                .respondToNewPasswordChallenge(username, newPassword)
                .pipe(
                  tapResponse({
                    next: () => {
                      messageService.show({
                        type: 'success',
                        message: 'Successfully updated password',
                      });
                      void navigationService.navigateTo('/login');
                    },
                    error: (error) => {
                      const err = error as Error;
                      messageService.show({
                        type: 'error',
                        message: 'Password updating failed',
                        detail: err?.message ?? String(error),
                        duration: 5000,
                      });
                    },
                    finalize: () => authDispatch.setLoading(false),
                  }),
                ),
            ),
          ),
        logout$: events.on(authEvents.logout).pipe(
          tap(() => authDispatch.setLoading(true)),
          tap(() => {
            authService.clearSession();
            authDispatch.logoutSuccess();
            cleanupService.cleanupAppStores();
            navigationService.navigateToLogin();
          }),
          tap(() => authDispatch.setLoading(false)),
        ),
        getUser$: events.on(authEvents.getUser).pipe(
          tap(() => authDispatch.setLoading(true)),
          switchMap(({ payload: { accessToken } }) =>
            authService.getUserInfo(accessToken).pipe(
              catchError((error) => {
                if (!isCognitoTokenError(error)) {
                  return throwError(() => error);
                }
                return authService.ensureFreshSession().pipe(
                  switchMap(() => {
                    const access = localStorage.getItem(
                      AUTH_STORAGE_KEYS.accessToken,
                    );
                    if (!access) {
                      return throwError(() => error);
                    }
                    return authService.getUserInfo(access);
                  }),
                );
              }),
              tapResponse({
                next: (getUserCommandOutput) => {
                  if (
                    getUserCommandOutput.UserAttributes &&
                    getUserCommandOutput.UserAttributes.length > 0
                  ) {
                    const attrs = getUserCommandOutput.UserAttributes;
                    const allTruthy = attrs.every((item) =>
                      Boolean(item.Value),
                    );
                    if (allTruthy) {
                      const userObject = authService.authUserFromAttr(attrs);
                      afterUserAttributesLoaded(
                        userObject,
                        { silent: true, flow: authAppFlow },
                        authDispatch,
                        messageService,
                        navigationService,
                      );
                    }
                  }
                },
                error: (error) => {
                  if (isAuthFatalError(error)) {
                    authDispatch.logout();
                  }
                },
                finalize: () => authDispatch.setLoading(false),
              }),
            ),
          ),
        ),
        loadTermsChecksum$: events
          .on(authEvents.loadTermsAndConditionsChecksum)
          .pipe(
            tap(() => authDispatch.setLoading(true)),
            switchMap(() =>
              authService.getTermsHash().pipe(
                tapResponse({
                  next: (checksum) => {
                    authDispatch.loadedTermsAndConditionsChecksum(checksum);
                  },
                  error: () => {
                    authDispatch.loadedTermsAndConditionsChecksum(null);
                  },
                  finalize: () => authDispatch.setLoading(false),
                }),
              ),
            ),
          ),
        acceptTerms$: events.on(authEvents.acceptTerms).pipe(
          tap(() => authDispatch.setLoading(true)),
          switchMap(({ payload: hash }) =>
            authService.acceptTerms(hash).pipe(
              tapResponse({
                next: () => {
                  authDispatch.termsAccepted(true);
                  const access = localStorage.getItem(
                    AUTH_STORAGE_KEYS.accessToken,
                  );
                  authDispatch.getUser({
                    accessToken: access ?? undefined,
                  });
                  messageService.show({
                    message: 'Terms accepted',
                    type: 'success',
                  });
                  navigationService.navigateTo('/booking/dashboard');
                },
                error: (err: unknown) => {
                  const e = err as Error;
                  messageService.show({
                    message: 'Could not record terms acceptance',
                    detail: e?.message ?? String(err),
                    type: 'error',
                    duration: 7000,
                  });
                },
                finalize: () => authDispatch.setLoading(false),
              }),
            ),
          ),
        ),
        loginSuccess$: events.on(authEvents.loginSuccess).pipe(
          switchMap(() => {
            const firebaseMessaging = injector.get(FirebaseMessagingService);
            return from(firebaseMessaging.getMessagingToken()).pipe(
              switchMap((fcmToken) => {
                if (!fcmToken) {
                  return of(null);
                }
                return notificationApi.registerPushToken(fcmToken).pipe(
                  tap(() => firebaseMessaging.listenForMessages()),
                  catchError((error: unknown) => {
                    const err = error as Error & { status?: number };
                    if (err.status === 0) {
                      console.warn(
                        '[FCM] Push token registration failed (network/CSP). ' +
                          'In Tauri, ensure connect-src allows the API host.',
                        err,
                      );
                    } else {
                      console.warn('[FCM] Push token registration failed', err);
                    }
                    return of(null);
                  }),
                );
              }),
            );
          }),
        ),
        loadTermsPresignedUrl$: events
          .on(authEvents.loadTermsAndConditionsPresignedUrl)
          .pipe(
            switchMap(() =>
              authService.getTermsPresignedUrl().pipe(
                tapResponse({
                  next: (response) => {
                    authDispatch.loadedTermsAndConditionsPresignedUrl(
                      response.upload_url,
                    );
                  },
                  error: (err: unknown) => {
                    const e = err as Error;
                    messageService.show({
                      type: 'error',
                      message: 'Failed to prepare terms upload',
                      detail: e?.message,
                      duration: 7000,
                    });
                  },
                }),
              ),
            ),
          ),
        updateTermsHashCommand$: events
          .on(authEvents.updateTermsHashCommand)
          .pipe(
            switchMap(() =>
              authService.updateTermsCommand().pipe(
                tapResponse({
                  next: () => {
                    messageService.show({
                      type: 'success',
                      message: 'Terms document updated',
                    });
                  },
                  error: (err: unknown) => {
                    const e = err as Error;
                    messageService.show({
                      type: 'error',
                      message: 'Update terms command failed',
                      detail: e?.message,
                      duration: 7000,
                    });
                  },
                }),
              ),
            ),
          ),
      };
    }),
  );
}
